import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Layout from "../Layout";
import { Button, TextBox } from "../../../../components";
import QuotationTenderingContext from "../QuotationTenderingContext";
import { Field, Form, Formik } from "formik";
import ComboBox from "../../../../components/common/ComboBox";
import { ReactComponent as TrashSvg } from "../../../../assets/admin/icons/trash.svg";
import {
  formatNumber,
  numberToOrdinal,
} from "../../../../helpers/common.helper";
import _ from "lodash";

const PaymentTerms = (props) => {
  const { setStep, details, setDetails, goForward, goBack } = useContext(
    QuotationTenderingContext
  );
  const [advancedPayment, setAdvancedPayment] = useState(
    details?.payment?.advancedPayment ?? {
      percentage: 10,
      remark: "",
    }
  );
  const [paymentTerms, setPaymentTerms] = useState(
    details?.payment?.paymentTerms ?? [
      [
        {
          item: undefined,
          remark: undefined,
          days: undefined,
          percentage: 90,
        },
      ],
    ]
  );
  const [wht] = useState(details?.payment?.wht);
  const [validateAfterSubmit, setValidateAfterSubmit] = useState(false);
  const formikRef = useRef(null);

  const _initialValues = useMemo(() => {
    let terms = new Array(paymentTerms.length + 1);

    terms[0] = [
      {
        item: "advancedPayments",
        remark: advancedPayment.remark,
        days: advancedPayment.days,
        percentage: advancedPayment.percentage,
      },
    ];

    let _no = 0;

    for (let i = 0; i < paymentTerms.length; i++) {
      let _tmp = new Array(paymentTerms[i].length);

      for (let j = 0; j < paymentTerms[i].length; j++) {
        _no += 1;

        const element = paymentTerms[i][j];

        let _term = {};

        if (element.item !== undefined) _term.item = element.item;
        if (element.remark !== undefined) _term.remark = element.remark;
        if (element.days !== undefined) _term.days = element.days;
        if (element.percentage !== undefined)
          _term.percentage = element.percentage;

        _tmp[j] = _term;

        // terms[_no] = _term;
      }

      terms[i + 1] = _tmp;
    }

    return { terms, wht };
  }, [advancedPayment, paymentTerms]);

  const _expectedGrandTotal = useMemo(
    () =>
      details?.items.reduce((prev, curr) => {
        return curr.price * curr.areaSize + prev;
      }, 0),
    [details?.items]
  );

  useEffect(() => {
    setStep(2);
  }, []);

  const handleValidate = (values, options) => {
    const { terms } = values;

    let errors = {};

    let totalPercentage = 0;

    let _terms = new Array(_initialValues.length);

    for (let i = 0; i < terms.length; i++) {
      const term = terms[i];
      const isFinalPayment = terms.length === i + 1;

      let itemError = new Array(term.length);

      for (let j = 0; j < term.length; j++) {
        const { item, remark, days: _days, percentage } = term[j];

        totalPercentage += percentage ?? 0;

        let termError = {};

        if (!item) {
          termError.item = "require";
        }

        // * Check duplicate item
        if (
          i > 0 &&
          item &&
          terms.find((t, tIdx) => {
            if (tIdx === 0) return false;

            let _match = t.find((_it, itemIdx) => {
              if (i === tIdx && j == itemIdx) {
                return false;
              }

              return item === _it.item;
            });

            return !_.isEmpty(_match);
          })
        ) {
          termError.item = "duplicate";
        }

        if (!isFinalPayment) {
          const days = Number(_days);
          if (!days || days <= 0) {
            termError.days = "require";
          }
        }

        if (!percentage || percentage < 0 || percentage > 100) {
          termError.percentage = "require";
        }

        if (_.isEmpty(termError)) continue;

        itemError[j] = termError;
      }

      _terms[i] = itemError;
    }

    // * Apply error to all percentage
    if (totalPercentage !== 100) {
      _terms = _terms.map((term) =>
        term.map((item) => {
          item.percentage = "over";

          return item;
        })
      );
    }

    // * Only apply error if an error exists (limitation due to values being array)
    if (_terms.find((t) => t.find((i) => i !== null) !== undefined))
      errors.terms = _terms;

    return errors;
  };

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);

    let current = _.cloneDeep(values.terms);
    const _advancedPayment = current.shift()[0];
    const _paymentTerms = current;

    setDetails((curr) => ({
      ...curr,
      payment: {
        advancedPayment: _advancedPayment,
        paymentTerms: _paymentTerms,
        wht: values.wht ?? false,
      },
    }));

    goForward();
  };

  return (
    <Layout>
      <Layout.Header>
        <h3>Payment Terms</h3>
        <Button
          label={"Add Payment Term"}
          size={"small"}
          onClick={() => {
            let current = _.cloneDeep(formikRef.current.values.terms);
            const _advancedPayment = current.shift()[0];
            const _paymentTerms = current;
            _paymentTerms.push([
              {
                item: undefined,
                remark: undefined,
                days: undefined,
                percentage: undefined,
              },
            ]);

            setAdvancedPayment(_advancedPayment);
            setPaymentTerms(_paymentTerms);
          }}
        />
      </Layout.Header>
      <Formik
        initialValues={_initialValues}
        validate={handleValidate}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validateOnChange={validateAfterSubmit}
        validateOnBlur={validateAfterSubmit}
        innerRef={formikRef}
      >
        {({ values, errors, touched, submitForm }) => {
          let _netTotal = 0;
          let _advancedTotal =
            ((values.terms[0][0].percentage ?? 0) * _expectedGrandTotal) / 100;

          _netTotal += _advancedTotal;

          const _getInputClass = (field, termIndex, itemIndex) => {
            if (!errors?.terms || !Array.isArray(errors.terms)) {
              return "tendering-quotation__design__payment-terms__table__body__item__input";
            }

            let termError = errors.terms[termIndex];

            if (_.isEmpty(termError) || _.isEmpty(termError[itemIndex])) {
              return "tendering-quotation__design__payment-terms__table__body__item__input";
            }

            if (!termError[itemIndex][field]) {
              return "tendering-quotation__design__payment-terms__table__body__item__input";
            }

            return "tendering-quotation__design__payment-terms__table__body__item__input tendering-quotation__design__payment-terms__table__body__item__input--error";
          };

          return (
            <>
              <Layout.Content className="tendering-quotation__design__payment-terms">
                <Form className="tendering-quotation__design__payment-terms__table-wrapper">
                  <table className="tendering-quotation__design__payment-terms__table">
                    <thead className="tendering-quotation__design__payment-terms__table__header">
                      <tr>
                        <th style={{ width: 20 }}>No</th>
                        <th style={{ width: 256 }}>Item</th>
                        <th style={{ minWidth: 256 }}>Remark</th>
                        {/* TODO: Add i icon to show full name Days for Submissions */}
                        <th style={{ width: 50 }}>Days</th>
                        <th style={{ width: 90 }}>Percentage (%)</th>
                        <th style={{ width: 120 }}>Amount</th>
                        <th style={{ width: 18 }}></th>
                      </tr>
                    </thead>
                    <tbody className="tendering-quotation__design__payment-terms__table__body">
                      <tr className="tendering-quotation__design__payment-terms__table__body__term-header">
                        <td
                          className="tendering-quotation__design__payment-terms__table__body__term-header__data"
                          colSpan={7}
                        >
                          Advanced Payment
                        </td>
                      </tr>
                      <tr>
                        <td>1</td>
                        <td>Advanced Payment</td>
                        <td>
                          <Field
                            className="tendering-quotation__design__payment-terms__table__body__item__input"
                            name="terms[0][0].remark"
                          />
                        </td>
                        <td>
                          <Field
                            className={_getInputClass("days", 0, 0)}
                            name="terms[0][0].days"
                            type="number"
                          />
                        </td>
                        <td>
                          <Field
                            className={_getInputClass("percentage", 0, 0)}
                            name="terms[0][0].percentage"
                            type="number"
                          />
                        </td>
                        <td className="tendering-quotation__design__payment-terms__table__body__item__total">
                          {formatNumber(_advancedTotal, "")}
                        </td>
                        <td />
                      </tr>
                      <tr className="tendering-quotation__design__payment-terms__table__body__term-summary">
                        <td
                          className="tendering-quotation__design__payment-terms__table__body__term-summary__title"
                          colSpan={5}
                        >
                          Sub Total
                        </td>
                        <td className="tendering-quotation__design__payment-terms__table__body__term-summary__data">
                          {formatNumber(_advancedTotal, "")}
                        </td>
                        <td className="tendering-quotation__design__payment-terms__table__body__term-summary__empty" />
                      </tr>
                      {paymentTerms.map((items, termIndex) => {
                        let _subTotal = 0;
                        const isFinalPayment =
                          paymentTerms.length === termIndex + 1;

                        return (
                          <>
                            <tr className="tendering-quotation__design__payment-terms__table__body__term-header">
                              <td
                                className="tendering-quotation__design__payment-terms__table__body__term-header__data"
                                colSpan={2}
                              >
                                {isFinalPayment
                                  ? "Final"
                                  : numberToOrdinal(termIndex + 1)}{" "}
                                Payment
                              </td>
                              <td
                                className="tendering-quotation__design__payment-terms__table__body__term-header__actions"
                                colSpan={5}
                              >
                                <div className="tendering-quotation__design__payment-terms__table__body__term-header__actions__content">
                                  <div
                                    className="tendering-quotation__design__payment-terms__table__body__term-header__actions__content__action"
                                    onClick={() => {
                                      let current = _.cloneDeep(values.terms);
                                      current.shift()[0];

                                      const _paymentTerms = current;

                                      _paymentTerms[termIndex].push({
                                        item: undefined,
                                        remark: undefined,
                                        days: undefined,
                                        percentage: undefined,
                                      });

                                      setPaymentTerms(_paymentTerms);
                                    }}
                                  >
                                    Add Item
                                  </div>
                                  {paymentTerms.length > 1 && (
                                    <div
                                      className="tendering-quotation__design__payment-terms__table__body__term-header__actions__content__action"
                                      onClick={() => {
                                        let current = _.cloneDeep(values.terms);
                                        const _advancedPayment =
                                          current.shift()[0];
                                        const _paymentTerms = current;
                                        _paymentTerms.splice(termIndex, 1);

                                        setAdvancedPayment(_advancedPayment);
                                        setPaymentTerms(_paymentTerms);
                                      }}
                                    >
                                      Delete
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {paymentTerms[termIndex].map((item, itemIndex) => {
                              let no = 0;

                              const _canDelete =
                                paymentTerms[termIndex].length > 1;

                              // * Calculate no
                              for (let i = 0; i < paymentTerms.length; i++) {
                                if (i > termIndex) break;

                                for (
                                  let j = 0;
                                  j < paymentTerms[i].length;
                                  j++
                                ) {
                                  if (i === termIndex && j > itemIndex) break;

                                  no += 1;
                                }
                              }

                              let _total = 0;

                              let _currentTerm = values?.terms[termIndex + 1];

                              if (_currentTerm && _currentTerm[itemIndex]) {
                                _total =
                                  ((_currentTerm[itemIndex]?.percentage ?? 0) *
                                    _expectedGrandTotal) /
                                  100;
                              }

                              _subTotal += _total;
                              _netTotal += _total;

                              return (
                                <tr
                                  className="tendering-quotation__design__payment-terms__table__body__item"
                                  key={`term-${itemIndex}`}
                                >
                                  <td>{no + 1}</td>
                                  <td>
                                    <Field
                                      className={_getInputClass(
                                        "item",
                                        termIndex + 1,
                                        itemIndex
                                      )}
                                      name={`terms[${
                                        termIndex + 1
                                      }][${itemIndex}].item`}
                                      as="select"
                                    >
                                      <option value="" disabled selected>
                                        Select an item
                                      </option>
                                      {details?.items?.map((item, index) => (
                                        <option value={index.toString()}>
                                          {item.name}
                                        </option>
                                      ))}
                                    </Field>
                                  </td>
                                  <td colSpan={isFinalPayment ? 2 : 1}>
                                    <Field
                                      className="tendering-quotation__design__payment-terms__table__body__item__input"
                                      name={`terms[${
                                        termIndex + 1
                                      }][${itemIndex}].remark`}
                                    />
                                  </td>
                                  {!isFinalPayment && (
                                    <td>
                                      <Field
                                        className={_getInputClass(
                                          "days",
                                          termIndex + 1,
                                          itemIndex
                                        )}
                                        name={`terms[${
                                          termIndex + 1
                                        }][${itemIndex}].days`}
                                        type="number"
                                      />
                                    </td>
                                  )}
                                  <td>
                                    <Field
                                      className={_getInputClass(
                                        "percentage",
                                        termIndex + 1,
                                        itemIndex
                                      )}
                                      name={`terms[${
                                        termIndex + 1
                                      }][${itemIndex}].percentage`}
                                      type="number"
                                    />
                                  </td>
                                  <td className="tendering-quotation__design__payment-terms__table__body__item__total">
                                    {formatNumber(_total, "")}
                                  </td>
                                  {_canDelete ? (
                                    <td
                                      className="tendering-quotation__design__payment-terms__table__body__item__delete"
                                      onClick={() => {
                                        let current = _.cloneDeep(values.terms);
                                        current.shift()[0];

                                        const _paymentTerms = current;
                                        _paymentTerms[termIndex].splice(
                                          itemIndex,
                                          1
                                        );

                                        setPaymentTerms(_paymentTerms);
                                      }}
                                    >
                                      <div className="tendering-quotation__design__payment-terms__table__body__item__delete__content">
                                        <TrashSvg width={16} height={16} />
                                      </div>
                                    </td>
                                  ) : (
                                    <td></td>
                                  )}
                                </tr>
                              );
                            })}
                            <tr className="tendering-quotation__design__payment-terms__table__body__term-summary">
                              <td
                                className="tendering-quotation__design__payment-terms__table__body__term-summary__title"
                                colSpan={5}
                              >
                                Sub Total
                              </td>
                              <td className="tendering-quotation__design__payment-terms__table__body__term-summary__data">
                                {formatNumber(_subTotal, "")}
                              </td>
                              <td className="tendering-quotation__design__payment-terms__table__body__term-summary__empty" />
                            </tr>
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </Form>
                <div className="tendering-quotation__design__payment-terms__summary">
                  <div className="tendering-quotation__design__payment-terms__summary__tax">
                    <label className="tendering-quotation__design__payment-terms__summary__tax__checkbox">
                      <Field type="checkbox" name="wht" />
                      Withhold Tax (3%)
                    </label>
                  </div>
                  <table className="tendering-quotation__design__payment-terms__summary__table">
                    <tr>
                      <td className="tendering-quotation__design__payment-terms__summary__table__title">
                        Net Total
                      </td>
                      <td className="tendering-quotation__design__payment-terms__summary__table__value">
                        {formatNumber(_netTotal, "")}
                      </td>
                    </tr>
                    <tr>
                      <td className="tendering-quotation__design__payment-terms__summary__table__title">
                        VAT (7%)
                      </td>
                      <td className="tendering-quotation__design__payment-terms__summary__table__value">
                        +{formatNumber(_netTotal * 0.07, "")}
                      </td>
                    </tr>
                    {values.wht && (
                      <tr>
                        <td className="tendering-quotation__design__payment-terms__summary__table__title">
                          WHT (3%)
                        </td>
                        <td className="tendering-quotation__design__payment-terms__summary__table__value">
                          -{formatNumber(_netTotal * 1.07 * 0.03, "")}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td className="tendering-quotation__design__payment-terms__summary__table__title">
                        Grand Total
                      </td>
                      <td className="tendering-quotation__design__payment-terms__summary__table__value">
                        {formatNumber(
                          _netTotal * 1.07 * (values.wht ? 0.97 : 1),
                          ""
                        )}
                      </td>
                    </tr>
                  </table>
                </div>
              </Layout.Content>
              <Layout.Footer>
                <Button label="Return" variant={"secondary"} onClick={goBack} />
                <Button
                  type="submit"
                  label="Continue"
                  onClick={() => {
                    setValidateAfterSubmit(true);
                    submitForm();
                  }}
                />
              </Layout.Footer>
            </>
          );
        }}
      </Formik>
    </Layout>
  );
};

PaymentTerms.propTypes = {};

export default PaymentTerms;
